import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../../../utils/init-firebase';
import { MeetingRockDiscussion, COLLECTION_NAMES } from '../../types';

interface RockDiscussion {
  id: string;
  meetingId: Timestamp;
  rockId: string;
  flaggedForDiscussion: boolean;
  reviewed: boolean;
  flaggedAt: Timestamp | null;
  reviewedAt: Timestamp | null;
}

export function useRockDiscussions(meetingId: string | undefined) {
  const [discussions, setDiscussions] = useState<RockDiscussion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!meetingId) {
      setDiscussions([]);
      setLoading(false);
      return;
    }

    const meetingIdTimestamp = Timestamp.fromDate(new Date(meetingId));

    // Set up real-time listener
    const q = query(
      collection(db, COLLECTION_NAMES.MEETING_ROCK_DISCUSSIONS),
      where('meetingId', '==', meetingIdTimestamp)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const discussionsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            meetingId: data.meetingId,
            rockId: data.rockId,
            flaggedForDiscussion: data.flaggedForDiscussion || false,
            reviewed: data.reviewed || false,
            flaggedAt: data.flaggedAt || null,
            reviewedAt: data.reviewedAt || null,
          } as RockDiscussion;
        });
        setDiscussions(discussionsData);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching rock discussions:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [meetingId]);

  async function toggleFlagged(rockId: string) {
    if (!meetingId) return;

    try {
      const meetingIdTimestamp = Timestamp.fromDate(new Date(meetingId));
      const existingQuery = query(
        collection(db, COLLECTION_NAMES.MEETING_ROCK_DISCUSSIONS),
        where('meetingId', '==', meetingIdTimestamp),
        where('rockId', '==', rockId)
      );
      const existingSnapshot = await getDocs(existingQuery);

      if (!existingSnapshot.empty) {
        const existing = existingSnapshot.docs[0];
        const existingData = existing.data();
        const newFlaggedState = !existingData.flaggedForDiscussion;

        await updateDoc(existing.ref, {
          flaggedForDiscussion: newFlaggedState,
          flaggedAt: newFlaggedState ? Timestamp.now() : null,
          updatedAt: Timestamp.now(),
        });

        if (newFlaggedState) {
          // Increment counters on rock
          const rockRef = doc(db, COLLECTION_NAMES.ROCKS, rockId);
          const rockDocSnap = await getDoc(rockRef);
          
          if (rockDocSnap.exists()) {
            const rockData = rockDocSnap.data();
            const currentConsecutive = (rockData.off_track_consecutive_count || 0) as number;
            const currentTotal = (rockData.off_track_total_count || 0) as number;

            await updateDoc(rockRef, {
              off_track_consecutive_count: currentConsecutive + 1,
              off_track_total_count: currentTotal + 1,
              updatedAt: Timestamp.now(),
            });
          }
        }
      } else {
        // Create new discussion entry
        await addDoc(collection(db, COLLECTION_NAMES.MEETING_ROCK_DISCUSSIONS), {
          meetingId: meetingIdTimestamp,
          rockId,
          flaggedForDiscussion: true,
          reviewed: false,
          flaggedAt: Timestamp.now(),
          reviewedAt: null,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });

        // Increment counters on rock
        const rockRef = doc(db, COLLECTION_NAMES.ROCKS, rockId);
        const rockDocSnap = await getDoc(rockRef);
        
        if (rockDocSnap.exists()) {
          const rockData = rockDocSnap.data();
          const currentConsecutive = (rockData.off_track_consecutive_count || 0) as number;
          const currentTotal = (rockData.off_track_total_count || 0) as number;

          await updateDoc(rockRef, {
            off_track_consecutive_count: currentConsecutive + 1,
            off_track_total_count: currentTotal + 1,
            updatedAt: Timestamp.now(),
          });
        }
      }
    } catch (error) {
      console.error('Error toggling flagged status:', error);
      throw error;
    }
  }

  async function markAsReviewed(rockId: string) {
    if (!meetingId) return;

    try {
      const meetingIdTimestamp = Timestamp.fromDate(new Date(meetingId));
      const existingQuery = query(
        collection(db, COLLECTION_NAMES.MEETING_ROCK_DISCUSSIONS),
        where('meetingId', '==', meetingIdTimestamp),
        where('rockId', '==', rockId)
      );
      const existingSnapshot = await getDocs(existingQuery);

      if (!existingSnapshot.empty) {
        const existing = existingSnapshot.docs[0];
        await updateDoc(existing.ref, {
          reviewed: true,
          reviewedAt: Timestamp.now(),
          flaggedForDiscussion: false,
          flaggedAt: null,
          updatedAt: Timestamp.now(),
        });
      } else {
        await addDoc(collection(db, COLLECTION_NAMES.MEETING_ROCK_DISCUSSIONS), {
          meetingId: meetingIdTimestamp,
          rockId,
          flaggedForDiscussion: false,
          reviewed: true,
          flaggedAt: null,
          reviewedAt: Timestamp.now(),
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
      }
    } catch (error) {
      console.error('Error marking as reviewed:', error);
      throw error;
    }
  }

  function isFlagged(rockId: string): boolean {
    return discussions.some(
      (d) => d.rockId === rockId && d.flaggedForDiscussion
    );
  }

  function isReviewed(rockId: string): boolean {
    return discussions.some((d) => d.rockId === rockId && d.reviewed);
  }

  function getFlaggedCount(): number {
    return discussions.filter((d) => d.flaggedForDiscussion).length;
  }

  return {
    discussions,
    loading,
    toggleFlagged,
    markAsReviewed,
    isFlagged,
    isReviewed,
    getFlaggedCount,
  };
}

